import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Star, Zap, Building2 } from 'lucide-react';

type BillingCycle = 'monthly' | 'annual';

interface Plan {
  name: string;
  icon: React.ElementType;
  description: string;
  monthlyPrice: string;
  annualPrice: string;
  highlighted: boolean;
  badge?: string;
  features: string[];
  cta: string;
  ctaLink: string;
}

const plans: Plan[] = [
  {
    name: 'Essencial',
    icon: Zap,
    description: 'Para clínicas que estão começando a digitalizar sua operação.',
    monthlyPrice: 'XXX',
    annualPrice: 'XXX',
    highlighted: false,
    features: [
      'Até 3 profissionais',
      'Agenda integrada',
      'Prontuário eletrônico (COFFITO)',
      'Gestão de pacientes',
      'Faturamento básico',
      'Suporte por e-mail',
    ],
    cta: 'Começar agora',
    ctaLink: '/cadastro?plano=essencial',
  },
  {
    name: 'Profissional',
    icon: Star,
    description:
      'O plano mais completo para clínicas que querem escalar com eficiência.',
    monthlyPrice: 'XXX',
    annualPrice: 'XXX',
    highlighted: true,
    badge: 'Mais Popular',
    features: [
      'Até 10 profissionais',
      'Tudo do plano Essencial',
      'Pacotes de sessões e faturamento avançado',
      'Fechamento mensal automatizado',
      'Controle de faltas (3 strikes)',
      'Notificações via WhatsApp',
      'Relatórios e dashboards',
      'Portal do paciente',
      'Suporte prioritário',
    ],
    cta: 'Escolher Profissional',
    ctaLink: '/cadastro?plano=profissional',
  },
  {
    name: 'Enterprise',
    icon: Building2,
    description:
      'Para redes de clínicas e operações com múltiplas unidades.',
    monthlyPrice: 'XXX',
    annualPrice: 'XXX',
    highlighted: false,
    badge: 'Multi-Clínica',
    features: [
      'Profissionais ilimitados',
      'Tudo do plano Profissional',
      'Multi-tenant (várias unidades)',
      'Branding personalizado por clínica',
      'API de integração',
      'Exportação TISS (planos de saúde)',
      'Gerente de conta dedicado',
      'SLA de suporte 4h',
      'Onboarding assistido',
    ],
    cta: 'Falar com vendas',
    ctaLink: '/contato?assunto=enterprise',
  },
];

const PricingSection = () => {
  const [billing, setBilling] = useState<BillingCycle>('annual');

  return (
    <section id="precos" className="py-20 md:py-28 bg-slate-950 relative overflow-hidden">
      {/* Background decorativo */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 left-1/4 h-[500px] w-[500px] rounded-full bg-cyan-500/5 blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 h-[400px] w-[400px] rounded-full bg-violet-500/5 blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="inline-flex rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3 py-1 text-sm font-medium text-cyan-300 mb-4"
          >
            Planos e preços
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-4xl font-display font-bold text-white mb-4"
          >
            Invista na eficiência da sua clínica
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-lg text-slate-400"
          >
            Escolha o plano ideal para o tamanho e a ambição da sua operação.
            Todos incluem atualizações gratuitas e migração assistida.
          </motion.p>
        </div>

        {/* Toggle mensal / anual */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex items-center justify-center gap-3 mb-12"
        >
          <span
            className={`text-sm font-medium transition-colors ${
              billing === 'monthly' ? 'text-white' : 'text-slate-500'
            }`}
          >
            Mensal
          </span>
          <button
            onClick={() =>
              setBilling((prev) =>
                prev === 'monthly' ? 'annual' : 'monthly'
              )
            }
            className="relative h-7 w-12 rounded-full bg-white/10 border border-white/20 transition-colors hover:bg-white/15"
            aria-label="Alternar entre mensal e anual"
            aria-pressed={billing === 'annual'}
          >
            <span
              className={`absolute left-0.5 top-0.5 h-6 w-6 rounded-full bg-cyan-400 shadow-md transition-transform duration-200 ${
                billing === 'annual' ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
          <span
            className={`text-sm font-medium transition-colors ${
              billing === 'annual' ? 'text-white' : 'text-slate-500'
            }`}
          >
            Anual
          </span>
          <span
            className={`ml-2 inline-flex rounded-full bg-emerald-500/20 border border-emerald-500/30 px-2.5 py-0.5 text-xs font-semibold text-emerald-400 transition-opacity duration-200 ${
              billing === 'annual' ? 'opacity-100' : 'opacity-0'
            }`}
            aria-hidden={billing !== 'annual'}
          >
            2 meses grátis
          </span>
        </motion.div>

        {/* Cards de preço */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => {
            const Icon = plan.icon;
            const price =
              billing === 'monthly' ? plan.monthlyPrice : plan.annualPrice;

            return (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative rounded-2xl p-[1px] ${
                  plan.highlighted
                    ? 'bg-gradient-to-b from-cyan-400 via-cyan-500/50 to-violet-500/50'
                    : 'bg-white/10'
                }`}
              >
                {/* Badge */}
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <span
                      className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${
                        plan.highlighted
                          ? 'bg-cyan-400 text-slate-950'
                          : 'bg-violet-500/80 text-white'
                      }`}
                    >
                      {plan.badge}
                    </span>
                  </div>
                )}

                <div
                  className={`rounded-2xl p-6 md:p-8 h-full flex flex-col ${
                    plan.highlighted
                      ? 'bg-slate-900'
                      : 'bg-slate-900/80'
                  }`}
                >
                  {/* Ícone e nome */}
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                        plan.highlighted
                          ? 'bg-cyan-500/20'
                          : 'bg-white/5'
                      }`}
                    >
                      <Icon
                        className={`h-5 w-5 ${
                          plan.highlighted
                            ? 'text-cyan-400'
                            : 'text-slate-400'
                        }`}
                      />
                    </div>
                    <h3 className="text-xl font-display font-bold text-white">
                      {plan.name}
                    </h3>
                  </div>

                  <p className="text-sm text-slate-400 mb-6 leading-relaxed">
                    {plan.description}
                  </p>

                  {/* Preço */}
                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-sm text-slate-500">R$</span>
                      <span
                        className={`text-4xl font-display font-bold ${
                          plan.highlighted ? 'text-white' : 'text-slate-200'
                        }`}
                      >
                        {price}
                      </span>
                      <span className="text-sm text-slate-500">/mês</span>
                    </div>
                    {billing === 'annual' && (
                      <p className="text-xs text-slate-500 mt-1">
                        Cobrado anualmente (R$ XXX/ano)
                      </p>
                    )}
                    {billing === 'monthly' && (
                      <p className="text-xs text-emerald-400/70 mt-1">
                        Economize R$ XXX/ano
                        no plano anual
                      </p>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-8 flex-1">
                    {plan.features.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-start gap-2.5 text-sm text-slate-300"
                      >
                        <Check
                          size={16}
                          className={`mt-0.5 shrink-0 ${
                            plan.highlighted
                              ? 'text-cyan-400'
                              : 'text-slate-500'
                          }`}
                        />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <Link
                    to={plan.ctaLink}
                    className={`block text-center py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                      plan.highlighted
                        ? 'bg-cyan-500 hover:bg-cyan-400 text-slate-950 shadow-lg shadow-cyan-500/25'
                        : 'bg-white/5 hover:bg-white/10 border border-white/20 text-white'
                    }`}
                  >
                    {plan.cta}
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Garantia */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center mt-12"
        >
          <p className="text-sm text-slate-500">
            Todos os planos incluem{' '}
            <strong className="text-slate-400">14 dias de teste grátis</strong>,
            sem necessidade de cartão de crédito.
            <br />
            Cancele a qualquer momento sem multas ou burocracia.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
